import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../../../utils/ToastContext'
import { trpc } from '../../../../../utils/trpc'
import type { User } from '../../../../users/types'

type ReviewersByGroupId = {
	groupId: string
	reviewers: { externalId: string; name: string }[]
}

export const useCycleReviewersList = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const toast = useToast()
	const [reviewersByGroups, setReviewersByGroups] = useState<ReviewersByGroupId[]>([])
	const [originalReviewersByGroups, setOriginalReviewersByGroups] = useState<ReviewersByGroupId[]>([])
	const [errorByGroup, setErrorByGroup] = useState<Record<string, string>>({})
	const [confirmDialogState, setConfirmDialogState] = useState<{
		isOpen: boolean
		message: string
		onConfirm: (() => void) | null
	}>({
		isOpen: false,
		message: '',
		onConfirm: null,
	})
	const { organizationId, cycleId, participantExternalId } = useParams<{
		organizationId: string
		cycleId: string
		participantExternalId: string
	}>()

	const backUrl = `/organizations/${organizationId}/cycles/${cycleId}/reviewers/list`

	const { data: cycle, isLoading: cycleLoading } = useQuery({
		...trpc.admin.cycles.getById.queryOptions({
			id: cycleId || '',
		}),
		enabled: !!cycleId,
	})

	const { data: cycleReviewer, isLoading: cycleReviewerLoading } = useQuery({
		...trpc.admin.cycles.reviewers.show.queryOptions({
			cycleId: cycleId || '',
			participantExternalId: participantExternalId || '',
		}),
		enabled: !!participantExternalId,
	})

	const { data: reviewersGroups, isLoading: reviewersGroupsLoading } = useQuery({
		...trpc.admin.cycles.reviewerGroups.search.queryOptions({
			cycleId: cycleId || '',
			search: '',
			perPage: 99999,
		}),
		enabled: !!cycleId,
	})

	const { data: hierarchy, isLoading: hierarchyLoading } = useQuery({
		...trpc.admin.hierarchies.getUserHierarchyByExternalId.queryOptions({
			organizationId: organizationId || '',
			externalId: participantExternalId || '',
			fileId: cycle?.hierarchyFileId || '',
		}),
		enabled: !!participantExternalId && !!cycle?.hierarchyFileId,
	})

	const editReviewerMutation = useMutation({
		...trpc.admin.cycles.reviewers.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Raters updated successfully')
				navigate(backUrl)
			},
			onError: () => {
				toast.showToast('Failed to update raters', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.show.queryKey({
						cycleId: cycleId || '',
						participantExternalId: participantExternalId || '',
					}),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.search.queryKey({ cycleId: cycleId || '' }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const addReviewersPostLaunchMutation = useMutation({
		...trpc.admin.cycles.reviewers.addPostLaunch.mutationOptions({
			onSuccess: (data) => {
				toast.showToast(
					`Successfully added ${data.reviewersAdded} reviewer(s) and created ${data.tasksCreated} task(s)`,
				)

				if (data.warnings.length > 0) {
					data.warnings.forEach((warning) => {
						toast.showToast(warning, 'info')
					})
				}

				navigate(backUrl)
			},
			onError: (error) => {
				toast.showToast(error.message || 'Failed to add reviewers', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.show.queryKey({
						cycleId: cycleId || '',
						participantExternalId: participantExternalId || '',
					}),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.search.queryKey({ cycleId: cycleId || '' }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const removeReviewerPostLaunchMutation = useMutation({
		...trpc.admin.cycles.reviewers.removePostLaunch.mutationOptions({
			onSuccess: (data) => {
				toast.showToast(`Successfully removed reviewer. ${data.tasksMarkedStale} task(s) marked as stale.`)

				if (data.warnings.length > 0) {
					data.warnings.forEach((warning) => {
						toast.showToast(warning, 'info')
					})
				}
			},
			onError: (error) => {
				toast.showToast(error.message || 'Failed to remove reviewer', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.show.queryKey({
						cycleId: cycleId || '',
						participantExternalId: participantExternalId || '',
					}),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewers.search.queryKey({ cycleId: cycleId || '' }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleRemoveReviewer = (value: { id: string; label: string }[], groupId: string) => {
		const newReviewers = value.map((v) => ({
			externalId: v.id,
			name: v.label,
		}))
		setReviewersByGroups((prev) => {
			const newReviewersByGroups = [...prev]
			const groupIndex = newReviewersByGroups.findIndex((r) => r.groupId === groupId)
			if (groupIndex !== -1) {
				newReviewersByGroups[groupIndex].reviewers = newReviewers
			}
			validateReviewersByGroups(newReviewersByGroups)
			return newReviewersByGroups
		})
	}

	const handleAddReviewer = (users: User[], groupId: string) => {
		const newReviewers = users.map((user) => ({
			externalId: user.externalId,
			name: `${user.firstName} ${user.lastName}`,
		}))
		setReviewersByGroups((prev) => {
			const newReviewersByGroups = [...prev]

			const groupIndex = newReviewersByGroups.findIndex((r) => r.groupId === groupId)

			if (groupIndex !== -1) {
				const existingExternalIds = new Set(newReviewersByGroups[groupIndex].reviewers.map((r) => r.externalId))
				const uniqueNewReviewers = newReviewers.filter((reviewer) => !existingExternalIds.has(reviewer.externalId))

				newReviewersByGroups[groupIndex].reviewers = [
					...newReviewersByGroups[groupIndex].reviewers,
					...uniqueNewReviewers,
				]
			} else {
				newReviewersByGroups.push({
					groupId,
					reviewers: newReviewers,
				})
			}
			validateReviewersByGroups(newReviewersByGroups)
			return newReviewersByGroups
		})
	}

	const validateReviewersByGroups = (reviewersByGroups: ReviewersByGroupId[]) => {
		const errors: Record<string, string> = {}
		const cumulativeMinReviewers = cycle?.cumulativeMinReviewers || 0
		const cumulativeMaxReviewers = cycle?.cumulativeMaxReviewers || 0

		const totalReviewers = reviewersByGroups.reduce((sum, group) => sum + group.reviewers.length, 0)

		reviewersByGroups.forEach((group) => {
			const groupData = reviewersGroups?.data.find((g) => g.id === group.groupId)
			const groupMin = groupData?.minReviewers
			const groupMax = groupData?.maxReviewers

			if (groupMin !== undefined && group.reviewers.length < groupMin) {
				errors[group.groupId] = 'Min raters not met. '
			}

			if (groupMax !== undefined && group.reviewers.length > groupMax) {
				errors[group.groupId] = `Max raters exceeded by ${group.reviewers.length - groupMax}. `
			}
		})

		if (totalReviewers < cumulativeMinReviewers) {
			reviewersByGroups.forEach((group) => {
				const groupData = reviewersGroups?.data.find((g) => g.id === group.groupId)
				// Only show global min if this group doesn't have its own minReviewers
				if (groupData && (groupData.minReviewers === undefined || groupData.minReviewers === 0)) {
					errors[group.groupId] = `${errors[group.groupId] || ''}Global min not met. `
				}
			})
		}

		if (totalReviewers > cumulativeMaxReviewers) {
			reviewersByGroups.forEach((group) => {
				const groupData = reviewersGroups?.data.find((g) => g.id === group.groupId)
				// Only show global max if this group doesn't have its own maxReviewers
				if (groupData && (groupData.maxReviewers === undefined || groupData.maxReviewers === 0)) {
					errors[group.groupId] =
						`${errors[group.groupId] || ''}Global max exceeded by ${totalReviewers - cumulativeMaxReviewers}. `
				}
			})
		}

		setErrorByGroup(errors)

		const hasErrors = Object.keys(errors).length > 0

		return {
			minimumNotMet: totalReviewers < cumulativeMinReviewers ? cumulativeMinReviewers - totalReviewers : 0,
			maximumExceeded: totalReviewers > cumulativeMaxReviewers ? totalReviewers - cumulativeMaxReviewers : 0,
			hasErrors,
		}
	}

	const handleSaveChanges = async () => {
		const validationResult = validateReviewersByGroups(reviewersByGroups)

		if (validationResult.hasErrors) {
			return
		}

		const isCycleInProgress = cycle?.status === 'in-progress'

		if (isCycleInProgress) {
			// Detect removed and added reviewers
			const removedReviewers: Array<{
				reviewerExternalId: string
				reviewerGroupId: string
				reviewerName: string
			}> = []
			const addedReviewers: Array<{
				reviewerExternalId: string
				reviewerGroupId: string
			}> = []

			// Find removed reviewers
			originalReviewersByGroups.forEach((originalGroup) => {
				const currentGroup = reviewersByGroups.find((g) => g.groupId === originalGroup.groupId)
				const currentReviewerIds = new Set(currentGroup?.reviewers.map((r) => r.externalId) || [])

				originalGroup.reviewers.forEach((originalReviewer) => {
					if (!currentReviewerIds.has(originalReviewer.externalId)) {
						removedReviewers.push({
							reviewerExternalId: originalReviewer.externalId,
							reviewerGroupId: originalGroup.groupId,
							reviewerName: originalReviewer.name,
						})
					}
				})
			})

			// Find added reviewers
			reviewersByGroups.forEach((currentGroup) => {
				const originalGroup = originalReviewersByGroups.find((g) => g.groupId === currentGroup.groupId)
				const originalReviewerIds = new Set(originalGroup?.reviewers.map((r) => r.externalId) || [])

				currentGroup.reviewers.forEach((currentReviewer) => {
					if (!originalReviewerIds.has(currentReviewer.externalId)) {
						addedReviewers.push({
							reviewerExternalId: currentReviewer.externalId,
							reviewerGroupId: currentGroup.groupId,
						})
					}
				})
			})

			// Show confirmation dialog
			let confirmMessage = 'This cycle is in progress. '
			if (removedReviewers.length > 0) {
				confirmMessage += `${removedReviewers.length} reviewer(s) will be removed, their tasks marked as stale, and responses marked as deleted. `
			}
			if (addedReviewers.length > 0) {
				confirmMessage += `${addedReviewers.length} reviewer(s) will be added and tasks created for the current GROUP_EVALUATION step (if active). `
			}
			confirmMessage += 'Do you want to continue?'

			// Open confirmation dialog with the action to perform on confirm
			setConfirmDialogState({
				isOpen: true,
				message: confirmMessage,
				onConfirm: async () => {
					setConfirmDialogState({ isOpen: false, message: '', onConfirm: null })

					// Remove reviewers first (sequentially)
					for (const removed of removedReviewers) {
						try {
							await removeReviewerPostLaunchMutation.mutateAsync({
								cycleId: cycleId || '',
								participantExternalId: participantExternalId || '',
								reviewerExternalId: removed.reviewerExternalId,
								reviewerGroupId: removed.reviewerGroupId,
							})
						} catch (error) {
							console.error(`Failed to remove reviewer ${removed.reviewerName}:`, error)
							toast.showToast(`Failed to remove reviewer ${removed.reviewerName}`, 'error')
							return // Stop if removal fails
						}
					}

					// Then add new reviewers
					if (addedReviewers.length > 0) {
						addReviewersPostLaunchMutation.mutate({
							cycleId: cycleId || '',
							participantExternalId: participantExternalId || '',
							reviewers: addedReviewers,
						})
					} else {
						// If no reviewers to add, just navigate back
						toast.showToast('Changes saved successfully')
						navigate(backUrl)
					}
				},
			})
		} else {
			// For other cycle states, use the normal edit endpoint
			const reviewers = reviewersByGroups.flatMap((group) =>
				group.reviewers.map((reviewer) => ({
					reviewerExternalId: reviewer.externalId,
					reviewerGroupId: group.groupId,
				})),
			)

			editReviewerMutation.mutate({
				cycleId: cycleId || '',
				participantExternalId: participantExternalId || '',
				reviewers,
			})
		}
	}

	const handleNavigateToAddReviewers = (groupId: string) => {
		navigate(`/organizations/${organizationId}/cycles/${cycleId}/reviewers/list/${participantExternalId}/${groupId}`)
	}

	const handleNavigateBack = () => {
		navigate(backUrl)
	}

	const getExcludedUserIds = (groupId: string) => {
		if (!cycle?.allowDuplicates) {
			return new Set(reviewersByGroups.flatMap((group) => group.reviewers.map((r) => r.externalId)))
		}

		return new Set(reviewersByGroups.find((r) => r.groupId === groupId)?.reviewers.map((r) => r.externalId) || [])
	}

	useEffect(() => {
		if (cycleReviewer) {
			const reviewersData = cycleReviewer.groups.map((group) => ({
				groupId: group.id,
				reviewers: group.reviewers.map((reviewer) => ({
					externalId: reviewer.externalId,
					name: `${reviewer.firstName} ${reviewer.lastName}`,
				})),
			}))
			setReviewersByGroups(reviewersData)
			setOriginalReviewersByGroups(JSON.parse(JSON.stringify(reviewersData))) // Deep copy
		}
	}, [cycleReviewer])

	const headerData = [
		{
			label: 'Employee',
			value: cycleReviewer ? `${cycleReviewer.participant.firstName} ${cycleReviewer.participant.lastName}` : '',
		},
		{
			label: 'Primary Manager',
			value: hierarchy?.directReportTo
				? `${hierarchy.directReportTo.firstName} ${hierarchy.directReportTo.lastName}`
				: '-',
		},
		{
			label: 'Secondary Manager',
			value: hierarchy?.secondaryReportTo
				? `${hierarchy.secondaryReportTo.firstName} ${hierarchy.secondaryReportTo.lastName}`
				: '-',
		},
		{
			label: 'Status',
			value: 'Awaiting Launch',
		},
		{
			label: 'Cumulative Min Raters',
			value: cycle?.cumulativeMinReviewers || 0,
		},
		{
			label: 'Cumulative Max Raters',
			value: cycle?.cumulativeMaxReviewers || 0,
		},
	]

	return {
		cycle,
		cycleReviewer,
		reviewersGroups,
		hierarchy,
		reviewersByGroups,
		headerData,
		backUrl,
		organizationId,
		cycleId,
		participantExternalId,
		errorByGroup,
		isLoading: reviewersGroupsLoading || cycleLoading || cycleReviewerLoading || hierarchyLoading,
		handleRemoveReviewer,
		handleAddReviewer,
		handleSaveChanges,
		handleNavigateToAddReviewers,
		handleNavigateBack,
		getExcludedUserIds,
		confirmDialogState,
		handleConfirmDialogClose: () => setConfirmDialogState({ isOpen: false, message: '', onConfirm: null }),
	}
}
